import { useMemo, useState } from "react";
import xlsx from "json-as-xlsx";
import {
    Card,
    CardTitle,
    CardHeader,
    CardContent,
    CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import {
    Copy,
    Download,
    Sparkles,
    Share2,
    FileJson,
} from "lucide-react";

export function Playground() {
    const [jsonInput, setJsonInput] = useState("");
    const [copied, setCopied] = useState(false);

    const { parsed, data, error } = useMemo(() => {
        if (!jsonInput.trim()) return { parsed: null, data: [], error: null };

        try {
            const p = JSON.parse(jsonInput);
            if (Array.isArray(p)) return { parsed: p, data: p, error: null };
            if (typeof p === "object")
                return { parsed: p, data: [p], error: null };
            return { parsed: null, data: [], error: "Invalid JSON structure" };
        } catch {
            return { parsed: null, data: [], error: "Invalid JSON" };
        }
    }, [jsonInput]);

    const columns = useMemo(() => {
        if (!data.length) return [];
        return Array.from(new Set(data.flatMap((r) => Object.keys(r))));
    }, [data]);

    const pretty = () => {
        if (!parsed) return;
        setJsonInput(JSON.stringify(parsed, null, 2));
    };

    const copy = async () => {
        if (!parsed) return;
        await navigator.clipboard.writeText(JSON.stringify(parsed, null, 2));
        setCopied(true);
        setTimeout(() => setCopied(false), 1000);
    };

    const download = () => {
        if (!parsed) return;
        const blob = new Blob([JSON.stringify(parsed, null, 2)], {
            type: "application/json",
        });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "data.json";
        a.click();
        URL.revokeObjectURL(a.href);
    };

    const downloadXlsx = () => {
        if (!data.length) return;

        const normalized = data.map((row) => {
            const o: Record<string, any> = {};
            columns.forEach((c) => {
                o[c] = row[c] ?? "";
            });
            return o;
        });

        const sheets = [
            {
                sheet: "Data",
                columns: columns.map((c) => ({
                    label: c,
                    value: c,
                })),
                content: normalized,
            },
        ];

        xlsx(sheets, { fileName: "data" });
    };

    const share = async () => {
        if (!parsed || !navigator.share) return;
        await navigator.share({
            title: "JSON Data",
            text: JSON.stringify(parsed, null, 2),
        });
    };

    return (
        <div className="min-h-screen px-6 py-10">
            {/* HEADER */}
            <header className="mx-auto mb-10 max-w-7xl min-w-max">
                <h1 className="text-3xl font-semibold tracking-tight">
                    JSON to Table
                </h1>
                <p className="text-muted-foreground">
                    Paste JSON, instantly visualize it as a table.
                </p>
            </header>

            {/* CONTENT */}
            <div className="mx-auto min-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* INPUT */}
                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle>JSON Input</CardTitle>
                                <CardDescription>
                                    Object or array of objects
                                </CardDescription>
                            </div>

                            <div className="flex items-center gap-1">
                                <Action icon={Sparkles} label="Pretty" onClick={pretty} disabled={!parsed} />
                                <Action icon={Copy} label="Copy" onClick={copy} disabled={!parsed} />
                                <Action icon={Download} label="Download" onClick={download} disabled={!parsed} />
                                {navigator.share && (
                                    <Action icon={Share2} label="Share" onClick={share} disabled={!parsed} />
                                )}
                            </div>
                        </div>
                    </CardHeader>

                    <Separator />

                    <CardContent className="pt-6">
                        <textarea
                            rows={16}
                            value={jsonInput}
                            spellCheck={false}
                            onChange={(e) => setJsonInput(e.target.value)}
                            placeholder={`{\n  "name": "Rizkal",\n  "stack": "Bun + React"\n}`}
                            className="w-full resize-none rounded-md border bg-muted px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
                        />

                        {error && (
                            <p className="mt-3 text-sm text-destructive">{error}</p>
                        )}

                        {copied && (
                            <p className="mt-2 text-xs text-muted-foreground">
                                Copied to clipboard
                            </p>
                        )}
                    </CardContent>
                </Card>

                {/* OUTPUT */}
                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle>Table Output</CardTitle>
                                <CardDescription>
                                    Generated from your JSON
                                </CardDescription>
                            </div>
                            <div className="flex items-center gap-1">
                                <Action icon={Download} label="Download Excel" onClick={downloadXlsx} disabled={!parsed} />
                            </div>
                        </div>
                    </CardHeader>

                    <Separator />

                    <CardContent className="pt-6 overflow-auto">
                        {data.length === 0 ? (
                            <EmptyState />
                        ) : (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b">
                                        {columns.map((c) => (
                                            <th
                                                key={c}
                                                className="px-3 py-2 text-left font-medium text-muted-foreground"
                                            >
                                                {c}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((row, i) => (
                                        <tr key={i} className="border-b last:border-0">
                                            {columns.map((c) => (
                                                <td key={c} className="px-3 py-2">
                                                    {String(row[c] ?? "")}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

/* ---------- helpers ---------- */

function Action({
    icon: Icon,
    label,
    onClick,
    disabled,
}: {
    icon: any;
    label: string;
    onClick: () => void;
    disabled?: boolean;
}) {
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <Button
                    size="icon"
                    variant="ghost"
                    onClick={onClick}
                    disabled={disabled}
                >
                    <Icon className="h-4 w-4" />
                </Button>
            </TooltipTrigger>
            <TooltipContent>{label}</TooltipContent>
        </Tooltip>
    );
}

function EmptyState() {
    return (
        <div className="flex h-[360px] flex-col items-center justify-center gap-3 text-center text-muted-foreground">
            <FileJson className="h-8 w-8" />
            <p className="text-sm">No data to display</p>
            <p className="text-xs">
                Paste valid JSON to generate a table
            </p>
        </div>
    );
}
